import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserMenu from "../ui/components/UserMenu";

type MarkdownBlock =
  | { type: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "hr" }
  | { type: "blockquote"; lines: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

const INLINE_TOKEN_REGEX = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

const renderInlineText = (value: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  const tokenRegex = new RegExp(INLINE_TOKEN_REGEX.source, "g");
  let match = tokenRegex.exec(value);

  while (match) {
    const token = match[0];
    const index = match.index ?? 0;

    if (index > lastIndex) {
      nodes.push(value.slice(lastIndex, index));
    }

    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(
        <strong key={`b-${index}`}>{token.slice(2, token.length - 2)}</strong>
      );
    } else if (token.startsWith("*") && token.endsWith("*")) {
      nodes.push(<em key={`i-${index}`}>{token.slice(1, token.length - 1)}</em>);
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(<code key={`c-${index}`}>{token.slice(1, token.length - 1)}</code>);
    } else {
      nodes.push(token);
    }

    lastIndex = index + token.length;
    match = tokenRegex.exec(value);
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes;
};

const isTableDividerRow = (line: string) =>
  /^\|?(?:\s*:?-{3,}:?\s*\|)+\s*$/.test(line.trim());

const parseTableRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isSpecialLine = (line: string) => {
  const trimmed = line.trim();
  return (
    /^#{1,6}\s+/.test(trimmed) ||
    /^-{3,}$/.test(trimmed) ||
    /^-\s+/.test(trimmed) ||
    /^>\s?/.test(trimmed) ||
    trimmed.startsWith("|")
  );
};

const parseMarkdown = (source: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let index = 0;

  while (index < lines.length) {
    const current = lines[index];
    const trimmed = current.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (/^-{3,}$/.test(trimmed)) {
      blocks.push({ type: "hr" });
      index += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6,
        text: headingMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    if (/^-\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length) {
        const row = lines[index].trim();
        if (!/^-+\s+/.test(row)) break;
        items.push(row.replace(/^-+\s+/, "").trim());
        index += 1;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      while (index < lines.length) {
        const row = lines[index].trim();
        if (!/^>\s?/.test(row)) break;
        quoteLines.push(row.replace(/^>\s?/, "").trim());
        index += 1;
      }
      blocks.push({ type: "blockquote", lines: quoteLines });
      continue;
    }

    if (trimmed.startsWith("|")) {
      const tableLines: string[] = [];
      while (index < lines.length) {
        const row = lines[index].trim();
        if (!row.startsWith("|")) break;
        tableLines.push(row);
        index += 1;
      }

      if (tableLines.length > 0) {
        const headers = parseTableRow(tableLines[0]);
        const startRowIndex =
          tableLines.length > 1 && isTableDividerRow(tableLines[1]) ? 2 : 1;
        const rows = tableLines
          .slice(startRowIndex)
          .map((row) => parseTableRow(row))
          .filter((row) => row.some((cell) => cell.length > 0));

        blocks.push({ type: "table", headers, rows });
      }
      continue;
    }

    const paragraphLines: string[] = [trimmed];
    index += 1;
    while (index < lines.length) {
      const row = lines[index];
      const rowTrimmed = row.trim();
      if (!rowTrimmed || isSpecialLine(row)) {
        break;
      }
      paragraphLines.push(rowTrimmed);
      index += 1;
    }
    blocks.push({ type: "paragraph", text: paragraphLines.join(" ") });
  }

  return blocks;
};

export default function SaasPlansPage() {
  const navigate = useNavigate();
  const [markdownSource, setMarkdownSource] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("/docs/saas_plans_show_in_app.md");
        if (!response.ok) {
          throw new Error(`Failed to load plans content (${response.status}).`);
        }
        const text = await response.text();
        setMarkdownSource(text);
      } catch (loadError) {
        if (loadError instanceof Error) {
          setError(loadError.message);
        } else {
          setError("Failed to load plans content.");
        }
      } finally {
        setLoading(false);
      }
    };

    void loadMarkdown();
  }, []);

  const markdownBlocks = useMemo(
    () => parseMarkdown(markdownSource),
    [markdownSource]
  );

  return (
    <div className="app">
      <nav className="topnav">
        <div className="logo">
          <div className="logo-mark">M</div>
          CaseFlow
        </div>
        <div className="nav-right">
          <button type="button" className="btn btn-sm" onClick={() => navigate("/owner")}>
            <i className="ti ti-arrow-left" /> Owner Dashboard
          </button>
          <UserMenu initials="FO" />
        </div>
      </nav>

      <div className="page active">
        <div className="saas-plans-layout">
          <div className="saas-plans-shell">
            <section className="card saas-plans-head">
              <div className="owner-overview-eyebrow">Billing</div>
              <h1>SAAS Plans</h1>
            </section>

            <section className="card saas-plans-content">
              {loading ? <div className="saas-plans-loading">Loading plans...</div> : null}
              {!loading && error ? (
                <div className="notice notice-red">
                  <i className="ti ti-alert-triangle" />
                  <span>{error}</span>
                </div>
              ) : null}
              {!loading && !error ? (
                <article className="saas-markdown">
                  {markdownBlocks.map((block, blockIndex) => {
                    if (block.type === "hr") {
                      return <hr key={`hr-${blockIndex}`} />;
                    }

                    if (block.type === "heading") {
                      if (block.level === 1) {
                        return <h1 key={`h1-${blockIndex}`}>{renderInlineText(block.text)}</h1>;
                      }
                      if (block.level === 2) {
                        return <h2 key={`h2-${blockIndex}`}>{renderInlineText(block.text)}</h2>;
                      }
                      if (block.level === 3) {
                        return <h3 key={`h3-${blockIndex}`}>{renderInlineText(block.text)}</h3>;
                      }
                      if (block.level === 4) {
                        return <h4 key={`h4-${blockIndex}`}>{renderInlineText(block.text)}</h4>;
                      }
                      if (block.level === 5) {
                        return <h5 key={`h5-${blockIndex}`}>{renderInlineText(block.text)}</h5>;
                      }
                      return <h6 key={`h6-${blockIndex}`}>{renderInlineText(block.text)}</h6>;
                    }

                    if (block.type === "paragraph") {
                      return (
                        <p key={`p-${blockIndex}`}>{renderInlineText(block.text)}</p>
                      );
                    }

                    if (block.type === "list") {
                      return (
                        <ul key={`ul-${blockIndex}`}>
                          {block.items.map((item, itemIndex) => (
                            <li key={`li-${blockIndex}-${itemIndex}`}>
                              {renderInlineText(item)}
                            </li>
                          ))}
                        </ul>
                      );
                    }

                    if (block.type === "blockquote") {
                      return (
                        <blockquote key={`q-${blockIndex}`}>
                          {block.lines.map((line, lineIndex) => (
                            <p key={`q-line-${blockIndex}-${lineIndex}`}>
                              {renderInlineText(line)}
                            </p>
                          ))}
                        </blockquote>
                      );
                    }

                    return (
                      <div className="saas-markdown-table-wrap" key={`t-${blockIndex}`}>
                        <table className="saas-markdown-table">
                          <thead>
                            <tr>
                              {block.headers.map((cell, cellIndex) => (
                                <th key={`th-${blockIndex}-${cellIndex}`}>
                                  {renderInlineText(cell)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {block.rows.map((row, rowIndex) => (
                              <tr key={`tr-${blockIndex}-${rowIndex}`}>
                                {row.map((cell, cellIndex) => (
                                  <td key={`td-${blockIndex}-${rowIndex}-${cellIndex}`}>
                                    {renderInlineText(cell)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </article>
              ) : null}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
